import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import { MdInfo } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { PendingItemsTabs } from '@modules/advancement/pendingItems/constants';
import { pendingItemsActiveTabSel } from '@modules/advancement/pendingItems/duck';
import { isUserInSubUnitChecker } from '@modules/advancement/pendingItems/utils';
import { GroupBy } from '@modules/advancement/unitAdvancement/components/GroupBy/GroupBy';
import { useGetAllSubUnitsWithYouth } from '@modules/advancement/unitAdvancement/duck/hooks';
import { groupBySubUnitToApprove } from '@modules/advancement/unitAdvancement/duck/utilsTyped';
import {
  ApproveAdvancementsButton,
  DeclineAdvancementsButton,
} from '@progress/components';
import {
  Tooltip,
  advancementStatuses,
  navigateToPurchaseOrderDetails,
  selectedOrganizationSel,
} from '@shared';
import { FilterButton, PaginationTotal, Table } from '@shared/components';
import { Program } from '@shared/constants';
import { hasNewPermissionSel } from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { isInactiveUnitSel } from '@unit/duck/selectorsTyped';
import { toggleArrayItem } from '@utils';

import { setFromActivityLogs } from '../../../../../progress/approveAdvancements';
import PendingItemsFilterCard from '../PendingItemsFilterCard';
import PendingItemsCustomRow from './PendingItemsCustomRow';
import styles from './PendingItemsTable.less';
import columns from './pendingItemsTableColumns';
import advancementsTabColumns from './pendingItemsTableColumnsTyped';

const PendingItemsTable = ({
  items,
  loading,
  sorter,
  showFilter = true,
  selectedRowKeys,
  isOffline,
  onSetSelectedRowKeys,
  onGoToYouthProfile,
  onGoToAdultProfile,
  onSorterChange,
  showPOID,
  showUpdate = true,
  isAwarding = false,
  awardTabs = false,
}) => {
  const dispatch = useDispatch();
  const canApprove = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.APPROVE_ADVANCEMENTS),
  );
  const canAward = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.AWARD_ADVANCEMENTS),
  );
  const showUpdateButton = (canApprove || canAward) && showUpdate;
  const activeTab = useSelector(pendingItemsActiveTabSel);
  const currentOrg = useSelector(selectedOrganizationSel);
  const isInactiveUnit = useSelector(isInactiveUnitSel);

  const showDeclineButton = activeTab === PendingItemsTabs.ACTIVITIES;
  const areAnyAdvancementsSelected = selectedRowKeys.length > 0;
  const selectedAdvancements = useMemo(
    () => items.filter(({ key }) => selectedRowKeys.includes(key)),
    [items, selectedRowKeys],
  );
  const handleSelectAll = useCallback(
    isSelectAll => {
      const selectedItemsKeys = isSelectAll ? items.map(({ key }) => key) : [];
      onSetSelectedRowKeys(selectedItemsKeys);
    },
    [items, onSetSelectedRowKeys],
  );

  const disableDecline = !areAnyAdvancementsSelected;

  const handleSelectRow = useCallback(
    record => {
      if (!isUserInSubUnitChecker(record) || record.isHeader || !showUpdate)
        return;

      const newSelectedKeys = toggleArrayItem(selectedRowKeys, record.key);
      onSetSelectedRowKeys(newSelectedKeys);
    },
    [showUpdate, selectedRowKeys, onSetSelectedRowKeys],
  );

  const handleTableChange = useCallback(
    (_pagination, _filters, sorter) => {
      onSorterChange(sorter);
    },
    [onSorterChange],
  );

  const handleGoToPODetails = useCallback(
    poid => {
      dispatch(navigateToPurchaseOrderDetails(poid));
    },
    [dispatch],
  );

  const onGoToPODetails = showPOID ? handleGoToPODetails : false;

  const renderPaginationTotal = useCallback(
    total => (
      <div className={styles.pendinTablePaginationContainer}>
        <Tooltip
          title={
            <FormattedMessage id="advancement.PendingItems.totalDisclaimer" />
          }
        >
          <MdInfo className={styles.infoIcon} />
        </Tooltip>
        <PaginationTotal total={total} selected={selectedRowKeys.length} />
      </div>
    ),
    [selectedRowKeys],
  );

  const pagination = {
    showTotal: renderPaginationTotal,
  };

  const canEditMySubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_SUB_UNIT),
  );
  const canEditAllSubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_UNIT),
  );
  const canSeeYouthProfiles = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.VIEW_YOUTH_PROFILE_PAGE),
  );
  const canAwardAdvancement = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.AWARD_ADVANCEMENTS),
  );

  const shouldDisableRecord = useCallback(
    record => {
      if (isInactiveUnit) return true;

      if (isAwarding && canAwardAdvancement) return false;

      if (!canEditMySubUnits && !canEditAllSubUnits && canSeeYouthProfiles)
        return true;

      if (!canEditMySubUnits && canEditAllSubUnits) return true;

      if (
        canEditMySubUnits &&
        !canEditAllSubUnits &&
        isUserInSubUnitChecker(record)
      )
        return true;

      return !record.userId;
    },
    [
      canAwardAdvancement,
      canEditAllSubUnits,
      canEditMySubUnits,
      canSeeYouthProfiles,
      isAwarding,
      isInactiveUnit,
    ],
  );

  const rowSelection = {
    selectedRowKeys,
    columnTitle: <></>, //See SBL-6847
    onChange: onSetSelectedRowKeys,
    onSelectAll: handleSelectAll,
    getCheckboxProps: record => {
      if (record.isHeader || !isAwarding) {
        return {
          disabled: true,
          style: { display: 'none' },
        };
      }

      if (shouldDisableRecord(record)) {
        return {
          disabled: true,
        };
      }
    },
  };

  useEffect(() => {
    dispatch(setFromActivityLogs(false));
  }, [dispatch]);

  // ADVANCEMENT TAB GROUPING
  const [isGroupBy, setIsGroupBy] = useState(null);
  const [isGroupByTemp, setIsGroupByTemp] = useState(null);

  const handleChange = value => {
    setIsGroupByTemp(value);
  };

  const handleSubmit = () => {
    setIsGroupBy(isGroupByTemp);
  };

  const { data: allSubUnitsWithYouth, isLoading } =
    useGetAllSubUnitsWithYouth();
  const newItems =
    isGroupBy === 'subUnit' &&
    [Program.BOY_SCOUT, Program.CUB_SCOUT].includes(currentOrg.program)
      ? groupBySubUnitToApprove(allSubUnitsWithYouth, items)
      : items;

  const renderTitle = () => (
    <React.Fragment>
      {showUpdateButton && (
        <ApproveAdvancementsButton
          className={styles.approveAdvancements}
          disabled={isOffline || !areAnyAdvancementsSelected}
          personsAdvancements={selectedAdvancements}
          advancementStatus={isAwarding ? advancementStatuses.AWARDED : null}
        />
      )}
      {showUpdateButton && showDeclineButton && (
        <DeclineAdvancementsButton
          className={styles.approveAdvancements}
          disabled={disableDecline}
          personsAdvancements={selectedAdvancements}
        />
      )}
      {showFilter && !showPOID && (
        <PendingItemsFilterCard>
          <FilterButton />
        </PendingItemsFilterCard>
      )}
      {showPOID && !isLoading && (
        <GroupBy
          onChange={handleChange}
          onSubmit={handleSubmit}
          groupByStatus={isGroupByTemp}
          awardTabs={awardTabs}
        />
      )}
    </React.Fragment>
  );

  return (
    <Table
      className={styles.table}
      size="small"
      fixedLayout
      dataSource={newItems}
      loading={loading}
      rowKey="key"
      title={renderTitle}
      columns={
        showPOID
          ? advancementsTabColumns({
              sorter,
              onGoToYouthProfile,
              onGoToAdultProfile,
              onGoToPODetails,
            })
          : columns({
              sorter,
              onGoToYouthProfile,
              onGoToAdultProfile,
              onGoToPODetails,
              shouldDisableRecord,
            })
      }
      pagination={pagination}
      sorter={sorter}
      rowSelection={rowSelection}
      components={{
        body: {
          row: PendingItemsCustomRow,
        },
      }}
      onRow={record => ({
        onClick: () => handleSelectRow(record),
        shouldDisableRecord,
      })}
      rowClassName={record =>
        record.isHeader ? styles.rowItemGroup : styles.rowItem
      }
      onChange={handleTableChange}
    />
  );
};

PendingItemsTable.propTypes = {
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  sorter: PropTypes.object.isRequired,
  isOffline: PropTypes.bool.isRequired,
  showFilter: PropTypes.bool,
  selectedRowKeys: PropTypes.array,
  onSetSelectedRowKeys: PropTypes.func,
  onGoToYouthProfile: PropTypes.func,
  onGoToAdultProfile: PropTypes.func,
  onSorterChange: PropTypes.func,
  showPOID: PropTypes.bool,
  showUpdate: PropTypes.bool,
  isAwarding: PropTypes.bool,
  awardTabs: PropTypes.bool,
};

export default PendingItemsTable;
